import { useEffect, useState } from "react";
import { select } from "slate";
import {
    AddParticipantsDocument,
    AddParticipantsMutation,
    AddParticipantsMutationVariables,
    AddTicketStateIdDocument,
    AddTicketStateIdMutation,
    AddTicketStateIdMutationVariables,
    ArtificialIntelligenceValue,
    AssignChatsDocument,
    AssignChatsMutation,
    AssignChatsMutationVariables,
    Chat, ChatAssignInput, ChatStatus,
    DeleteMessageDocument, DeleteMessageMutation, DeleteMessageMutationVariables,
    DeliverScheduledMessageDocument, DeliverScheduledMessageMutation, DeliverScheduledMessageMutationVariables,
    EditMessageDocument, EditMessageMutation,
    EditMessageMutationVariables, EndChatDocument, EndChatMutation, EndChatMutationVariables, GetChatDocument,
    GetChatQuery, GetChatQueryVariables, GetParticipantsForChatDocument,
    GetParticipantsForChatQuery, GetParticipantsForChatQueryVariables, GetTicketCardDocument,
    GetTicketCardQuery, GetTicketCardQueryVariables, GetTicketDocument, GetTicketQuery, GetTicketQueryVariables, HoldChatsDocument, HoldChatsMutation, HoldChatsMutationVariables, IgnoreDocument, IgnoreMutation, IgnoreMutationVariables, MarkReadDocument,
    MarkReadMutation, MarkReadMutationVariables, MarkUnreadDocument, MarkUnreadMutation,
    MarkUnreadMutationVariables, PortalEventType, PortalMessageEvent,
    PortalParticipantEvent,
    PortalReactionEvent,
    PortalSender,
    PortalTicketChangedField,
    RedactMessageDocument,
    RedactMessageMutation,
    RedactMessageMutationVariables,
    RemoveParticipantsDocument,
    RemoveParticipantsMutation, RemoveParticipantsMutationVariables,
    SaveDraftDocument, SaveDraftMutation, SaveDraftMutationVariables,
    SaveTicketDocument,
    SaveTicketMutation,
    SaveTicketMutationVariables,
    SendMessageDocument,
    SendMessageMutation,
    SendMessageMutationVariables,
    SendNoteDocument,
    SendNoteMutation,
    SenderType,
    SnoozeDocument,
    SnoozeMutation,
    SnoozeMutationVariables,
    Ticket, TicketInput,
    TicketMessage,
    TicketMessageArticle,
    TicketMessageArticleInput,
    TicketMessageAttachment,
    TicketMessageStatusType,
    TicketMessageType, TicketPriority, TicketStatus,
    User,
} from "../../__generated__/api";
import client from "../../api";
import { UploadedFile } from "../../components/agent/text-entry/text-entry";
import { StripFormatting, santitize } from "../../components/html-render/HtmlRenderer";
import { tryParseDate } from "../../date";
import { LoggingHandler } from "../logging/LoggingContext";
import SelectionState from "./selection";
import TicketColumnGroup from "./ticket-group";
import TicketTasks from "./ticket-tasks";
import UserCache from "./user-cache";
import EventHandler from "../../event-handler";
import createId from "../../create-id";
import ColumnStateCache from "./column-state-cache";
import TicketState from "./ticket-state";

interface DraftProperties {
    id?: string;
    type: TicketMessageType;
    attachments?: UploadedFile[];
    attachmentReferences: TicketMessageAttachment[];
    body: string;
    scheduledDate?: Date;
    articles?: TicketMessageArticle[];
}
interface SelectionProperties {
    selectionStart: number;
    selectionLength: number;
}

interface TicketOverrides {
    lastReadTimestamp?: Date;
    isChecked?: boolean;
    ticketStateId?: string;
    draft?: DraftProperties;
    selection?: SelectionProperties;
}

const TEXT_EMPTY_SET = new Set([
    "",
    "<p></p>",
    "<p><br></p>",
]);
export const STATUS_ID = "urn:fq:form-field:status";
export const PRIORITY_ID = "urn:fq:form-field:priority";
export const SUBJECT_ID = "urn:fq:form-field:subject";
export const DESCRIPTION_ID = "urn:fq:form-field:description";
export const SEVERITY_ID = "urn:fq:form-field:severity";
export const REQUESTOR_ID = "urn:fq:form-field:requestorId";
export const TAGS_ID = "urn:fq:form-field:tags";
export const ARTIFICIAL_INTELLIGENCE_ID = "urn:fq:form-field:artificial-intelligence";

const FIELD_ID_TO_TICKET_FIELD: Record<string, keyof Ticket> = {
    [STATUS_ID]: "status",
    [PRIORITY_ID]: "priority",
    [TAGS_ID]: "tags",
    [SUBJECT_ID]: "subject",
    [DESCRIPTION_ID]: "description",
    [REQUESTOR_ID]: "requestorId",
    [ARTIFICIAL_INTELLIGENCE_ID]: "artificialIntelligence",
};

const CHAT_TO_TICKET_STATUS = {
    [ChatStatus.Assigned]: TicketStatus.Open,
    [ChatStatus.ChatRequested]: TicketStatus.Open,
    [ChatStatus.Connected]: TicketStatus.Open,
    [ChatStatus.DeflectionWaiting]: TicketStatus.Deflection,
    [ChatStatus.Finished]: TicketStatus.Closed,
    [ChatStatus.Ignored]: TicketStatus.Ignored,
    [ChatStatus.OnHold]: TicketStatus.Hold,
    [ChatStatus.Snoozed]: TicketStatus.Snooze,
};

function portalMessageToTicketMessage(message: PortalMessageEvent, current?: TicketMessage) {
    const editing = current ?? {} as TicketMessage;
    editing.body = message.message;
    editing.createdDate = editing.createdDate ?? tryParseDate(message.date);
    editing.id = message.messageId;
    editing.articles = message.articles;
    editing.type = message.type;
    editing.attachments = message.attachments?.map(row => ({
        urlSha256: row.urlSha256,
        url: row.imageUrl,
        fileName: row.title,
        mimeType: row.mimeType,
        previewImage: row.imageUrl,
        title: row.title,
    }));
    editing.reactions = message.reactions ?? editing.reactions;
    editing.creator = {
        id: message.sender?.id,
        avatarUrl: message.sender?.iconUrl,
        name: message.sender?.name,
    } as User;
    editing.creatorId = message.sender?.id;
    editing.isDeleted = message.isDeleted;
    editing.originalBody = message.originalBody;
    editing.redactedDate = tryParseDate(message.redactedDate);
    editing.status = message.status;
    return editing;
}

function isUnassigned(status: ChatStatus) {
    return status === ChatStatus.ChatRequested || status === ChatStatus.Connected;
}

export enum Events {
    ASSIGNED = "ASSIGNED",
    LISTENERS = "LISTENERS",
    DRAFT = "DRAFT"
}

export default class TicketItem extends EventHandler<Events> {
    private _state?: TicketOverrides;

    private _isChatLoaded = false;

    private _isReloading = false;

    private _ticket: Ticket;

    private _isArchived = false;

    private _chat: Chat;

    private _messages: TicketMessage[];

    private _tasks: TicketTasks;

    private _loadingWaiter?: Promise<void>;

    private _cardRef?: HTMLElement | null;

    private _group?: TicketColumnGroup;

    private _participants?: {isLoading: boolean, participants: User[] | undefined};

    private _aiValues?: ArtificialIntelligenceValue[];

    public constructor(
        chat: Chat,
        private selectionState: SelectionState,
        private userCache: UserCache,
        private ticketState?: TicketState,
    ) {
        super();
        this._chat = { ...chat };
        this._ticket = chat.ticket as Ticket ?? {};
        this._aiValues = [...chat.ticket?.artificialIntelligence?.values ?? []];
        this._messages = this._ticket.messages ?? [];
        this._tasks = new TicketTasks(this, () => this.updateListners(), userCache);
        this._tasks.append(chat.ticket?.tasks);
    }

    public setGroup(group: TicketColumnGroup) {
        this._group = group;
    }

    public setIsArchived(isArchived: boolean) {
        this._isArchived = isArchived;
    }

    public get isArchived() {
        return this._isArchived;
    }

    public reconnected() {
        this._loadingWaiter = undefined;
        this._isChatLoaded = false;
        this._isReloading = true;
    }

    public useChatDetails({ isTicketOnly }: { isTicketOnly?: boolean } = {}) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        this.useUpdates();

        if (isTicketOnly && !this._isChatLoaded && !this._loadingWaiter) {
            this._loadingWaiter = LoggingHandler.instance.nextTick(async () => {
                if (this._isChatLoaded) {
                    return;
                }
                await this.forceDownloadFullTicket();
            });
        } else if (!this._isChatLoaded && !this._loadingWaiter) {
            this._loadingWaiter = LoggingHandler.instance.nextTick(async () => {
                if (this._isChatLoaded) {
                    return;
                }
                await this.forceDownloadChat();
            });
        }

        return {
            isLoading: !this._isChatLoaded,
            isFirstLoading: !this._isChatLoaded && !this._isReloading,
            ticket: this,
            chat: this.chat,
        };
    }

    public useCardRef() {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return (value: HTMLElement | null) => {
            this._cardRef = value;
        };
    }

    public useUpdates() {
        this.useEvent(Events.LISTENERS);
        return this.chat;
    }

    private updateListners() {
        this.emit(Events.LISTENERS);
    }

    public scrollIntoViewWithUpdate() {
        this.selectionState.update(() => {
            this.scrollIntoView();
        });
    }

    public focusViewWithUpdate() {
        this.selectionState.update(() => {
            this.focus();
        });
    }

    public scrollIntoView(done?: () => void) {
        if (this._group?.isCollapsed) {
            this._group.expand();
            setTimeout(() => {
                this._cardRef?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                    inline: "center",
                });
                if (done) {
                    setTimeout(done, 500);
                }
            }, 100);
        } else {
            this._cardRef?.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "center",
            });
        }
    }

    public highlight() {
        this._cardRef?.classList.add("highlight");
        setTimeout(() => {
            this._cardRef?.classList.remove("highlight");
        }, 1000);
    }

    public focus() {
        const isVisible = this._cardRef?.checkVisibility();
        this.scrollIntoView();
        if (!isVisible) {
            this._cardRef?.classList.add("highlight-delay");
        }
        this._cardRef?.classList.add("highlight");
        setTimeout(() => {
            this._cardRef?.classList.remove("highlight");
            if (!isVisible) {
                this._cardRef?.classList.remove("highlight-delay");
            }
        }, 2000);
    }

    public setChat(chat: Chat) {
        if (!this._isChatLoaded) {
            this._chat = {
                ...chat,
            };
            this._ticket = {
                ...this.ticket,
                ...chat.ticket,
            };
            this._aiValues = [...this.ticket?.artificialIntelligence?.values ?? []];

            if (chat.ticket?.messages) {
                this.appendMessages(...chat.ticket.messages.map(row => ({
                    ...row,
                    createdDate: tryParseDate(row.createdDate),
                    reactions: row.reactions ? [...row.reactions] : [],
                } as TicketMessage)));
            }
            this._tasks.append(chat.ticket?.tasks);
        }
    }

    public saveTicket(ticket: Partial<TicketInput>) {
        this._ticket = {
            ...this._ticket,
            ...ticket,
        } as Ticket;
        this._aiValues = [...this.ticket?.artificialIntelligence?.values ?? []];
        this._chat = {
            ...this.chat,
            subject: this._ticket.subject,
            priority: this._ticket.priority,
        };
        this.updateListners();
        LoggingHandler.instance.wrap(async () => {
            const saved = await client.mutate<SaveTicketMutation, SaveTicketMutationVariables>({
                mutation: SaveTicketDocument,
                variables: {
                    id: this.ticket.id,
                    ticket,
                },
            });

            this._ticket = {
                ...this._ticket,
                ...saved.data?.saveTicket,
            } as Ticket;
            this.selectionState.update();
        });
    }

    public assignLocal(input: ChatAssignInput & {status?: ChatStatus; ticketStatus?: TicketStatus}) {
        return LoggingHandler.instance.wrap(async () => {
            let { assigneeId } = input;
            let user: User | undefined;

            let { ticketStatus } = input;
            if (!ticketStatus) {
                ticketStatus = input.status ? CHAT_TO_TICKET_STATUS[input.status] : undefined;
            }
            const shouldAssignSelf = input.shouldAssignSelf || assigneeId === this.userCache.currentUser?.id;
            if (input.shouldAssignSelf) {
                assigneeId = this.userCache.currentUser?.id;
                user = this.userCache.currentUser;
            }
            if (assigneeId || input.shouldAssignSelf) {
                user = user ?? (assigneeId ? await this.userCache.get(assigneeId) : undefined);
                assigneeId = user?.id;
                this._ticket = {
                    ...this._ticket,
                    assigneeId,
                    assignee: user ?? this._ticket.assignee,
                    status: ticketStatus ?? (shouldAssignSelf ? TicketStatus.Open : undefined),
                } as Ticket;
                this._chat = {
                    ...this.chat,
                    assigneeId,
                    assignee: user ?? this._chat.assignee,
                    isAssignedToCurrentUser: shouldAssignSelf,
                    status: input.status ?? (
                        shouldAssignSelf && isUnassigned(this._chat.status) ? ChatStatus.Assigned : this._chat.status
                    ),
                };
            } else if (input.shouldUnassign) {
                this._ticket = {
                    ...this._ticket,
                    assigneeId: undefined,
                    assignee: undefined,
                    status: ticketStatus ?? TicketStatus.Open,
                } as Ticket;
                this._chat = {
                    ...this.chat,
                    assigneeId: undefined,
                    assignee: undefined,
                    status: input.status ?? ChatStatus.ChatRequested,
                };
            }

            this.emit(Events.ASSIGNED);

            this.selectionState.update();
        });
    }

    public assign(input: ChatAssignInput) {
        return LoggingHandler.instance.all(
            this.assignLocal(input),
            client.mutate<AssignChatsMutation, AssignChatsMutationVariables>({
                mutation: AssignChatsDocument,
                variables: {
                    ids: [this.id],
                    assignment: {
                        ...input,
                        shouldAssignSelf: input.shouldAssignSelf || input.assigneeId === this.userCache.currentUser?.id,
                    },
                },
            }),
        );
    }

    public get participants() {
        return this._participants?.participants;
    }

    public get allUsers() {
        return this._participants?.participants?.concat([this.requester, this.ticket.assignee as User]).filterNull();
    }

    public useParticipants() {
        this.useUpdates();

        if (!this._participants?.isLoading && !this._participants) {
            LoggingHandler.instance.wrap(async () => {
                if (this._isChatLoaded) {
                    return;
                }
                const { data } = await client.query<GetParticipantsForChatQuery, GetParticipantsForChatQueryVariables>({
                    query: GetParticipantsForChatDocument,
                    variables: {
                        chatId: this.id,
                    },
                });
                this._participants = {
                    isLoading: false,
                    participants: (data.getParticipants ? [...data.getParticipants] : []) as User[],
                };
                this.updateListners();
            });
        }

        this._participants ??= {
            isLoading: true,
            participants: undefined,
        };

        return this._participants;
    }

    public processParticipants(type: PortalEventType, event: PortalParticipantEvent) {
        if (!this._participants?.participants || this._participants?.isLoading) {
            return;
        }
        if (type === PortalEventType.ParticipantsAdded) {
            this._participants.participants = this._participants
                .participants
                .concat(...(event.participants as unknown as User[]))
                .dedupe(row => row.id);
        } else {
            /* eslint-disable-next-line function-paren-newline */
            this._participants.participants = this._participants.participants?.exclude(
                event.participants as PortalSender[], row => row.id) ?? [];
        }
        this.updateListners();
    }

    public addParticipants(ids: string[], note?: string) {
        if (ids.isEmpty) {
            return;
        }

        const isCurrentUserParticipating = ids.includes(this.userCache.currentUser?.id);
        if (isCurrentUserParticipating) {
            this._chat = {
                ...this._chat,
                isCurrentUserParticipating,
            };
        }

        LoggingHandler.instance.wrap(async () => {
            if (this._participants) {
                const users = await this.userCache.getUsers(...ids);
                this._participants.participants?.push(...users);
            }
            this.updateListners();

            await client.mutate<AddParticipantsMutation, AddParticipantsMutationVariables>({
                mutation: AddParticipantsDocument,
                variables: {
                    participants: {
                        chatId: this.id,
                        note,
                        participantIds: ids,
                    },
                },
            });
        });
    }

    public removeParticipants(ids: string[], note?: string) {
        if (ids.isEmpty) {
            return;
        }

        if (ids.includes(this.userCache.currentUser?.id)) {
            this._chat = {
                ...this._chat,
                isCurrentUserParticipating: false,
            };
        }

        LoggingHandler.instance.wrap(async () => {
            if (this._participants) {
                const set = new Set(ids);
                this._participants.participants = this._participants?.participants?.filter(row => !set.has(row.id)) ?? [];
            }
            this.updateListners();
            await client.mutate<RemoveParticipantsMutation, RemoveParticipantsMutationVariables>({
                mutation: RemoveParticipantsDocument,
                variables: {
                    participants: {
                        chatId: this.id,
                        note,
                        participantIds: ids,
                    },
                },
            });
        });
    }

    public get aiValues() {
        return this._aiValues;
    }

    public set aiValues(value: ArtificialIntelligenceValue[] | undefined) {
        this._aiValues = value;
        this.updateListners();
    }

    public get chat(): Omit<Chat, "ticket"> {
        return this._chat;
    }

    public get ticket(): Omit<Ticket, "messages"> {
        return this._ticket;
    }

    public get messages(): TicketMessage[] {
        return this._messages;
    }

    public get tasks(): TicketTasks {
        return this._tasks;
    }

    public get requester() {
        return this.ticket?.requestor ?? this.ticket?.creator ?? this.chat.creator;
    }

    public get id() {
        return this.chat.id;
    }

    public get status() {
        return this.chat.status;
    }

    public get priority() {
        return this._ticket.priority ?? this.chat.priority ?? TicketPriority.Medium;
    }

    public setPriorityLocal(priority: TicketPriority) {
        this._chat = {
            ...this._chat,
            priority,
        };
        this._ticket = {
            ...this._ticket,
            priority,
        };
        this.updateListners();
    }

    public get ticketStateId() {
        return this.state.ticketStateId ?? this.chat.stateId;
    }

    public get latestDraft() {
        let latest = this.state.draft;
        if (!latest) {
            const messageDraft = this.messages?.find(message => message.type === TicketMessageType.Draft);
            if (messageDraft) {
                latest = {
                    id: messageDraft.id,
                    body: messageDraft.body.html as string,
                    articles: messageDraft.articles as TicketMessageArticle[] | undefined,
                    // draft: messageDraft,
                    attachmentReferences: messageDraft.attachments as TicketMessageAttachment[],
                    type: TicketMessageType.AgentMessage,
                };
            }
        }

        return latest;
    }

    public async saveDraftWithUpdate(draft: DraftProperties) {
        await this.saveDraft(draft);
        this.emit(Events.DRAFT);
    }

    public async saveDraft(draft: DraftProperties) {
        return LoggingHandler.instance.wrap(async () => {
            const latest = this.latestDraft;
            const isTextSame = (draft.body === latest?.body) || (TEXT_EMPTY_SET.has(draft.body) && !latest?.body);
            if (isTextSame
                && draft.articles?.equals(latest?.articles ?? [])
                && draft.attachments?.equals(latest?.attachments ?? [])
                && draft.attachmentReferences?.equals(latest?.attachmentReferences ?? [])
            ) {
                return;
            }

            this.setState({
                ...this.state,
                draft: {
                    ...draft,
                    id: latest?.id,
                    body: draft.body,
                    articles: draft.articles,
                    attachments: [],
                    attachmentReferences: draft
                        .attachmentReferences?.concat(draft?.attachmentReferences ?? [])
                        .dedupe(row => row.urlSha256 ?? row.url),

                },
            });

            const savedDraft = await client.mutate<SaveDraftMutation, SaveDraftMutationVariables>({
                mutation: SaveDraftDocument,
                variables: {
                    chatId: this.id,
                    message: {
                        draftId: latest?.id,
                        body: draft.body,
                        scheduledDate: draft.scheduledDate,
                        attachmentTokens: draft.attachments?.toIdList("token").filterNull(),
                        attachmentReferences: draft.attachmentReferences.map(({ urlSha256 }) => ({ urlSha256 })),
                        articles: draft.articles?.map(article => ({
                            id: article.id,
                            portalId: article.portalId,
                            previewText: article.previewText,
                            title: article.title,
                        })),
                    },
                },
            });

            if (savedDraft.data?.storeMessageDraft?.id) {
                this.setState({
                    ...this.state,
                    draft: {
                        ...draft,
                        id: savedDraft.data?.storeMessageDraft?.id,
                        body: draft.body,
                        articles: draft.articles,
                        attachments: [],
                        attachmentReferences: draft
                            .attachmentReferences.concat(savedDraft.data?.storeMessageDraft.attachments ?? [])
                            .dedupe(row => row.urlSha256 ?? row.url),

                    },
                });
            }
        });
    }

    public get selection() {
        return this.state.selection;
    }

    public async saveSelection(selection: SelectionProperties) {
        this.setState({
            ...this.state,
            selection,
        });
    }

    public get state(): TicketOverrides {
        return this._state ?? {
            lastReadTimestamp: tryParseDate(this.chat.currentUserLastReadDate) ?? new Date(),
            isChecked: false,
            ticketStateId: this.chat.stateId,
        };
    }

    public setTags(tags: string[]) {
        this._ticket = {
            ...this._ticket,
            tags,
        };
        this.updateListners();
        this.saveTicket({
            tags,
        });
    }

    public setState(values: TicketOverrides) {
        this._state = values;
        this.updateListners();
    }

    public get isOpened() {
        return this.selectionState.openItem === this;
    }

    public closeIfOpen() {
        if (!this.isOpened) {
            return;
        }

        this.selectionState.closeCurrent();
    }

    public markReadLocal() {
        this.setState({
            ...this.state,
            lastReadTimestamp: new Date(),
        });
        this.selectionState.update();
    }

    public markRead() {
        this.markReadLocal();
        LoggingHandler.instance.wrap(async () => {
            await client.mutate<MarkReadMutation, MarkReadMutationVariables>({
                mutation: MarkReadDocument,
                variables: {
                    chatIds: [this.id],
                },
            });
        });
    }

    public markUnreadLocal() {
        this.setState({
            ...this.state,
            lastReadTimestamp: new Date(0),
        });
        this.selectionState.update();
    }

    public markUnread() {
        this.markUnreadLocal();
        LoggingHandler.instance.wrap(async () => {
            await client.mutate<MarkUnreadMutation, MarkUnreadMutationVariables>({
                mutation: MarkUnreadDocument,
                variables: {
                    chatIds: [this.id],
                },
            });
        });
    }

    public get createDate() {
        return tryParseDate(this.chat.createdDate);
    }

    public get lastUserMessageDate() {
        return tryParseDate(this.chat.lastUserMessageDate);
    }

    public get lastAgentResponseDate() {
        return tryParseDate(this.chat.lastAgentResponseDate);
    }

    public get currentUserLastReadDate() {
        return this.state.lastReadTimestamp ?? tryParseDate(this.chat.currentUserLastReadDate);
    }

    public get isUnread() {
        return (this.lastUserMessageDate ?? this.createDate ?? new Date())?.getTime()
            > (this.currentUserLastReadDate?.getTime() ?? 0);
    }

    public get isChecked() {
        return this.state.isChecked ?? false;
    }

    public setIsChecked(value: boolean) {
        this.setState({
            ...this.state,
            isChecked: value,
        });
        if (value) {
            this.selectionState.addSelection(this);
        } else {
            this.selectionState.removeSelection(this);
        }
    }

    public setTicketStateId(ticketStateId: string, status?: ChatStatus) {
        this.setTicketStateIdLocal(ticketStateId, status);

        LoggingHandler.instance.wrap(async () => {
            await client.mutate<AddTicketStateIdMutation, AddTicketStateIdMutationVariables>({
                mutation: AddTicketStateIdDocument,
                variables: {
                    stateId: ticketStateId,
                    status,
                    chatIds: [this.id],
                },
            });
        });
    }

    public setTicketStateIdLocal(ticketStateId: string | undefined, status?: ChatStatus, assigneeId?: string) {
        const oldStatus = this.status;

        this._chat = {
            ...this._chat,
            stateId: ticketStateId,
            status: status ?? this._chat.status,
        };
        this._ticket = {
            ...this._ticket,
            ticketStateId,
            status: status ? CHAT_TO_TICKET_STATUS[status] : this._ticket.status,
        };

        const isBeforeUnassigned = !this._chat.assigneeId && isUnassigned(oldStatus);
        const isNewUnassigned = status ? isUnassigned(status) : undefined;

        if (isBeforeUnassigned && !isNewUnassigned) {
            this.assignLocal({
                shouldAssignSelf: !assigneeId,
                assigneeId,
                status,
            });
        } else if (!isBeforeUnassigned && isNewUnassigned) {
            this.assignLocal({
                shouldUnassign: true,
                status,
            });
        }

        this.setState({
            ...this.state,
            ticketStateId,
        });
    }

    public setStatusLocal(status: ChatStatus) {
        this._ticket = {
            ...this._ticket,
            status: CHAT_TO_TICKET_STATUS[status],
        };
        this._chat = {
            ...this._chat,
            status,
        };
        this.updateListners();
    }

    public async setStatus(status: ChatStatus) {
        this.setStatusLocal(status);

        const ticketState = this.ticketState?.columnStateCache?.getForTicket(this);
        if (ticketState?.id && ticketState?.id !== this.ticketStateId) {
            this.setTicketStateIdLocal(ticketState?.id);
        }
        this.assignLocal({
            status,
            shouldAssignSelf: status === ChatStatus.Assigned,
            shouldUnassign: status === ChatStatus.ChatRequested,
            assigneeId: status === ChatStatus.Assigned ? this.userCache.currentUser?.id : undefined,
        });
        await LoggingHandler.instance.wrap(async () => {
            if (status === ChatStatus.Assigned || status === ChatStatus.ChatRequested) {
                await LoggingHandler.instance.withRetries(
                    () => client.mutate<AssignChatsMutation, AssignChatsMutationVariables>({
                        mutation: AssignChatsDocument,
                        variables: {
                            ids: [this.id],
                            assignment: {
                                status,
                                shouldAssignSelf: status === ChatStatus.Assigned,
                                shouldUnassign: status === ChatStatus.ChatRequested,
                            } as ChatAssignInput,
                        },
                    }),
                );
            } else if (status === ChatStatus.Finished) {
                await LoggingHandler.instance.withRetries(
                    () => client.mutate<EndChatMutation, EndChatMutationVariables>({
                        mutation: EndChatDocument,
                        variables: {
                            chatEndMessages: [
                                {
                                    chatId: this.id,
                                },
                            ],
                        },
                    }),
                );
            } else if (status === ChatStatus.Ignored) {
                await LoggingHandler.instance.withRetries(
                    () => client.mutate<IgnoreMutation, IgnoreMutationVariables>({
                        mutation: IgnoreDocument,
                        variables: {
                            ids: [this.id],
                        },
                    }),
                );
            } else if (status === ChatStatus.OnHold) {
                await LoggingHandler.instance.withRetries(
                    () => client.mutate<HoldChatsMutation, HoldChatsMutationVariables>({
                        mutation: HoldChatsDocument,
                        variables: {
                            ids: [this.id],
                            options: {},
                        },
                    }),
                );
            } else if (status === ChatStatus.Snoozed) {
                await LoggingHandler.instance.withRetries(
                    () => client.mutate<SnoozeMutation, SnoozeMutationVariables>({
                        mutation: SnoozeDocument,
                        variables: {
                            ids: [this.id],
                            options: {},
                        },
                    }),
                );
            }
        });
    }

    private async forceDownloadChat() {
        const data = await LoggingHandler.instance.withRetries(
            () => client.query<GetChatQuery, GetChatQueryVariables>({
                fetchPolicy: "network-only",
                query: GetChatDocument,
                variables: {
                    id: this.id,
                },
            }),
        ) ?? {};

        if (data) {
            this._isReloading = false;
            this._isChatLoaded = true;

            if (data.chat) {
                this.assignChat(data.chat as Chat);
            }
        }
        this.updateListners();
    }

    private async forceDownloadFullTicket() {
        const data = await LoggingHandler.instance.withRetries(
            () => client.query<GetTicketQuery, GetTicketQueryVariables>({
                fetchPolicy: "network-only",
                query: GetTicketDocument,
                variables: {
                    id: this.ticket.id,
                },
            }),
        ) ?? {};

        if (data) {
            this._isReloading = false;
            this._isChatLoaded = true;

            if (data.ticket) {
                this.assignChat({
                    ticket: data.ticket as Ticket,
                });
            }
        }
        this.updateListners();
    }

    private assignChat(chat: Partial<Chat>) {
        if (chat.ticket) {
            this._ticket = {
                ...this.ticket,
                ...chat.ticket,
            };
        }
        this._chat = {
            ...this._chat,
            ...chat,
            ticket: this._ticket,
        };
        this._aiValues = [...this.ticket?.artificialIntelligence?.values ?? []];
        if (chat.ticket?.messages) {
            this.appendMessages(...chat.ticket.messages.map(row => ({
                ...row,
                createdDate: tryParseDate(row.createdDate),
                reactions: row.reactions ? [...row.reactions] : [],
            } as TicketMessage)));
        }
        this._tasks.append(chat?.ticket?.tasks);
    }

    public resync(chat: Chat) {
        this.assignChat(chat);
        this.selectionState.update();
        LoggingHandler.instance.info({ id: this.id }, "Resync Completed");
    }

    public resyncTicket() {
        return LoggingHandler.instance.time({ id: this.id }, "Ticket Resync Completed", async () => {
            const { data } = await client.query<GetTicketCardQuery, GetTicketCardQueryVariables>({
                query: GetTicketCardDocument,
                fetchPolicy: "network-only",
                variables: {
                    id: this.ticket.id,
                },
            });

            if (data.ticket) {
                const { status } = this.ticket;
                this._ticket = {
                    ...this.ticket,
                    ...data.ticket,
                } as Ticket;

                this._aiValues = [...this.ticket?.artificialIntelligence?.values ?? []];

                if (status !== this.ticket.status) {
                    await this.forceDownloadChat();
                } else {
                    this.updateListners();
                }
            }
        });
    }

    public processTicketUpdate(changes: PortalTicketChangedField[]) {
        LoggingHandler.instance.wrap(async () => {
            await LoggingHandler.instance.wrap(() => changes.forEachConcurrent(async change => {
                const systemField = FIELD_ID_TO_TICKET_FIELD[change.fieldId];
                if (systemField) {
                    this._ticket = {
                        ...this._ticket,
                        [systemField]: change.current?.string
                            ?? change.current?.number
                            ?? change.current?.date
                            ?? change.current?.boolean
                            ?? change.current?.options,
                    };

                    if (systemField === "artificialIntelligence" && change.current?.string) {
                        this._ticket.artificialIntelligence = JSON.parse(change.current?.string);
                        this._aiValues = [...this._ticket?.artificialIntelligence?.values ?? []];
                    }

                    if (systemField === "requestorId") {
                        this._ticket.requestor = await this.userCache.get(this._ticket.requestorId) ?? this._ticket.requestor;
                    }
                    this._chat = {
                        ...this._chat,
                        subject: this._ticket.subject,
                        priority: this.ticket.priority,
                    };
                } else {
                    const customField = this.ticket.customFields?.find(field => field.formFieldId === change.fieldId);
                    if (!customField) {
                        return;
                    }

                    this._ticket = {
                        ...this._ticket,
                        customFields: this.ticket.customFields?.map(field => {
                            if (field.formFieldId === change.fieldId) {
                                return {
                                    ...field,
                                    displayValue: change.current?.displayValue,
                                    value: change.current?.string
                                        ?? change.current?.number
                                        ?? change.current?.date
                                        ?? change.current?.boolean
                                        ?? change.current?.options,
                                };
                            }
                            return field;
                        }),
                    };
                }
            }));
            this.selectionState.update();
            this.updateListners();
        });
    }

    public processDeleted() {
        this.closeIfOpen();
        this.updateListners();
    }

    public processMessageEvent(event: PortalEventType, msg: PortalMessageEvent): {shouldNotify: boolean} {
        if (event === PortalEventType.MessageNew
             || event === PortalEventType.NoteAdded
              || event === PortalEventType.SystemMessage) {
            this.appendMessage({
                status: {
                    status: TicketMessageStatusType.Success,
                },
                ...msg,
            });

            if (msg.type !== TicketMessageType.AgentMessage
                && !msg.sender?.isCurrentUser
                && msg.sender?.id !== this.userCache.currentUser?.id) {
                this.chat.lastUserMessageDate = tryParseDate(msg.date);
            } else if (msg.type === TicketMessageType.AgentMessage) {
                this.chat.lastAgentResponseDate = tryParseDate(msg.date);
            }

            const isUserMessage = event === PortalEventType.MessageNew && msg.type === TicketMessageType.UserMessage;

            const isAssigneeOrParticipant = this.chat.assignee == null
                || this.chat.isAssignedToCurrentUser
                || this.chat.isCurrentUserParticipating === true;

            const shouldNotify = !msg.sender?.isCurrentUser && isUserMessage && isAssigneeOrParticipant;
            const shouldMark = !msg.sender?.isCurrentUser && (isUserMessage || isAssigneeOrParticipant);
            if (shouldMark) {
                if (this.isOpened) {
                    this.markRead();
                } else {
                    this.markUnread();
                }
            }
            return { shouldNotify };
        }
        if (event === PortalEventType.MessageEdited
            || event === PortalEventType.MessageDeleted) {
            this.messageEdited(event, msg.messageId, msg.message.html ?? "", msg);
        }
        if (event === PortalEventType.MessageRemoved) {
            this.removeMessage(msg.messageId);
        }
        if (event === PortalEventType.MessageRedacted) {
            this.redactMessageLocal(msg.messageId);
        }
        if (event === PortalEventType.MessageStatus) {
            this.processMessageStatus(msg);
        }

        return { shouldNotify: false };
    }

    public appendMessages(...messages: TicketMessage[]) {
        const seen = new Set();
        this._messages = [...(this._messages ?? []), ...messages]
            .reduce((previous, current) => {
                let hasSeen = seen.has(current.id);
                seen.add(current.id);
                current?.messageIds?.forEach(id => {
                    if (seen.has(id)) {
                        hasSeen = true;
                    }
                    seen.add(id);
                });
                if (!hasSeen) {
                    previous.push(current);
                }

                return previous;
            }, [] as TicketMessage[])
            .map(row => ({
                ...row,
                createdDate: tryParseDate(row.createdDate),
            }))
            .sortBy(row => row.createdDate?.getTime()) as TicketMessage[];
    }

    public appendMessage(message: PortalMessageEvent) {
        if (message.sender?.type === SenderType.User) {
            this._chat.lastMessage = message.message.plaintext;
            this._chat.lastUserMessageDate = tryParseDate(message.date);
        }

        const sourceSet = new Set([...message.sourceMessageIds, message.messageId]);
        const sentMessage = this._messages.find(msg => sourceSet.has(msg.id));
        if (sentMessage) {
            sentMessage.id = message.messageId;
            sentMessage.status = message.status ?? sentMessage.status;
            return;
        }

        this.appendMessages(portalMessageToTicketMessage(message));

        this.selectionState.update();
    }

    public sendMessage(message: SendMessage) {
        LoggingHandler.instance.wrap(async () => {
            try {
                const sendMessage = await client.mutate<SendMessageMutation, SendMessageMutationVariables>({
                    mutation: message.type === TicketMessageType.AgentMessage ? SendMessageDocument : SendNoteDocument,
                    variables: {
                        chatId: this.id,
                        message: {
                            draftId: this.latestDraft?.id,
                            body: message.body,
                            scheduledDate: message.scheduledDate,
                            attachmentTokens: message.attachmentTokens,
                            attachmentReferences: message.attachmentReferences.map(({ urlSha256 }) => ({ urlSha256 })),
                            articles: message.type === TicketMessageType.AgentMessage
                                ? message.articles?.map(article => ({
                                    id: article.id,
                                    portalId: article.portalId,
                                    previewText: article.previewText,
                                    title: article.title,
                                }))
                                : undefined,
                        },
                    },
                });
                const draft = this.latestDraft;
                if (draft) {
                    this.setState({
                        ...this.state,
                        draft: undefined,
                    });
                    const index = this._messages.findIndex(msg => msg.id === draft.id);
                    if (index >= 0) {
                        this._messages.splice(index, 1);
                    }
                }

                this.appendMessages(
                    (sendMessage.data?.sendMessage ?? (sendMessage.data as SendNoteMutation)?.saveTicketNote) as TicketMessage,
                );
                this.chat.lastAgentResponseDate = new Date();
                this.updateListners();
            } catch (err) {
                this.appendMessages(
                    {
                        body: {
                            html: message.body,
                        },
                        creator: this.userCache.currentUser,
                        creatorId: this.userCache.currentUser?.id,
                        createdDate: new Date(),
                        id: `urn:fq:portal-ticket-message-error:${createId()}`,
                        type: TicketMessageType.AgentMessage,
                        status: {
                            status: TicketMessageStatusType.Error,
                            lines: [
                                {
                                    sourceName: "Agent Web",
                                    error: "Error occured while sending message. Please try resending your message",
                                },
                            ],
                        },

                    },
                );
                throw err;
            }
        });
    }

    public processMessageStatus(message: PortalMessageEvent) {
        const updatingMessage = this._messages.find(msg => msg.id === message.messageId
            || message.messageId in (msg.messageIds ?? []));
        if (updatingMessage) {
            updatingMessage.status = message.status;
            return;
        }
        this.selectionState.update();
    }

    public processReactionEvent(eventType: PortalEventType, event: PortalReactionEvent) {
        const msg = this.messages.find(current => current.id === event.messageId);
        if (!msg) {
            return;
        }

        msg.reactions ??= [];
        if (eventType === PortalEventType.ReactionAdded) {
            msg.reactions.push({
                creator: {
                    ...event.sender,
                } as User,
                creatorId: event.sender?.id,
                isCreatedByCurrentUser: event.sender?.isCurrentUser ?? false,
                reaction: event.reaction,
            });
            this.updateListners();
        }
    }

    private messageEdited(type: PortalEventType, messageId: string, body?: string, message?: PortalMessageEvent) {
        const msg = this.messages.find(current => current.id === messageId);
        if (!msg) {
            return;
        }
        if (message) {
            portalMessageToTicketMessage(message, msg);
        } else {
            msg.isDeleted = msg.isDeleted ?? type === PortalEventType.MessageDeleted;
            if (msg.type !== TicketMessageType.Scheduled) {
                msg.originalBody = msg.originalBody ?? msg.body;
            }
            if (body) {
                msg.body = {
                    html: body,
                    plaintext: santitize(body, { stripFormatting: StripFormatting.ALL }),
                };
            }

            if (type === PortalEventType.MessageRedacted) {
                msg.redactedDate = msg.redactedDate ?? new Date();
                msg.body = {};
                msg.originalBody = undefined;
                msg.attachments = [];
                msg.articles = [];
                msg.isDeleted = false;
            }
        }

        if (msg.type === TicketMessageType.UserMessage) {
            const lastMessage = this.messages.findLast(current => current.type === TicketMessageType.UserMessage);
            if (lastMessage === msg) {
                this.chat.lastMessage = msg.body.plaintext;
            }
        }
        this.updateListners();
    }

    private removeMessage(messageId: string) {
        const msgIndex = this.messages.findIndex(current => current.id === messageId);
        if (msgIndex < 0) {
            return;
        }
        this.messages.splice(msgIndex, 1);
        this.updateListners();
    }

    public editMessage(messageId: string, body: string | undefined) {
        return LoggingHandler.instance.wrap(async () => {
            this.messageEdited(PortalEventType.MessageEdited, messageId, body ?? "");
            await client.mutate<EditMessageMutation, EditMessageMutationVariables>({
                mutation: EditMessageDocument,
                variables: {
                    ticketMessageId: messageId,
                    message: {
                        body,
                    },
                },
            });
        });
    }

    public redactMessageLocal(messageId: string) {
        return LoggingHandler.instance.wrap(async () => {
            this.messageEdited(PortalEventType.MessageRedacted, messageId);
        });
    }

    public redactMessage(messageId: string) {
        return LoggingHandler.instance.wrap(async () => {
            this.redactMessageLocal(messageId);

            await client.mutate<RedactMessageMutation, RedactMessageMutationVariables>({
                mutation: RedactMessageDocument,
                variables: { ticketMessageId: messageId },
            });
        });
    }

    public deleteMessage(messageId: string) {
        return LoggingHandler.instance.wrap(async () => {
            const msg = this.messages.find(current => current.id === messageId);

            if (msg?.type === TicketMessageType.Scheduled) {
                this.removeMessage(messageId);
            } else {
                this.messageEdited(PortalEventType.MessageDeleted, messageId);
            }

            await client.mutate<DeleteMessageMutation, DeleteMessageMutationVariables>({
                mutation: DeleteMessageDocument,
                variables: { ticketMessageId: messageId },
            });
        });
    }

    public deliverScheduledMessage(messageId: string) {
        return LoggingHandler.instance.wrap(async () => {
            this.removeMessage(messageId);
            await client.mutate<DeliverScheduledMessageMutation, DeliverScheduledMessageMutationVariables>({
                mutation: DeliverScheduledMessageDocument,
                variables: { ticketMessageId: messageId },
            });
        });
    }

    public rescheduleMessage(messageId: string, date: Date) {
        return LoggingHandler.instance.wrap(async () => {
            const msg = this.messages.find(current => current.id === messageId);

            if (msg) {
                msg.schedule = { date };
                this.updateListners();
            }

            await client.mutate<EditMessageMutation, EditMessageMutationVariables>({
                mutation: EditMessageDocument,
                variables: {
                    ticketMessageId: messageId,
                    message: { schedule: { date } },
                },
            });
        });
    }
}

interface SendMessage {
    body: string;
    scheduledDate?: Date;
    attachmentTokens?: string[];
    attachmentReferences:TicketMessageAttachment[];
    articles?: TicketMessageArticleInput[];
    type: TicketMessageType
}
